// Anita Week 1 / Day 1 — Money, Choice and Value (U.S. Grade 3 math upgrade).
// Russian is the primary learning language; English text is supplied as
// secondary help via `enHelpText`. Math is U.S. Grade 3 level: two-step word
// problems, multiplication/division as steps, comparison of options, tables,
// simple fractions. Russian stays at RU Grade 1–2.
// SAFETY: paper simulations only. No real shop, no real currency.

const day1 = {
  id: 'day-1',
  studentId: 'anita',
  weekId: 'week-1',
  dayId: 'day-1',
  status: 'active',
  title: 'Деньги, выбор и ценность / Money, Choice and Value',
  shortTitle: 'Деньги и выбор / Money and Choice',
  theme:
    'Деньги помогают выбирать, но настоящая ценность появляется из пользы. / Money helps people choose, but real value comes from usefulness.',
  estimatedTimeLabel: '60–90 минут / 60–90 minutes',
  lessonTiming: { minMinutes: 60, maxMinutes: 90, breaks: 2 },
  bilingualMode: 'ru-primary-en-helper',
  courseLayers: ['reading', 'math', 'logic', 'spelling', 'creative', 'speech'],
  readingLevel: 'grade-2',
  nimnyIntro:
    'Нимни открывает первую денежную миссию. Сегодня Анита учится считать стоимость покупки, сравнивать два варианта и понимать, что главное — польза, а не цена. / Nimny opens the first money mission. Today Anita learns to add up a purchase, compare two options, and see that the main thing is usefulness, not price.',
  goals: [
    'Понять, что деньги — это инструмент выбора. / Understand that money is a tool for choice.',
    'Решать двухшаговые задачи: сначала стоимость, потом остаток. / Solve two-step word problems.',
    'Сравнивать два варианта покупки. / Compare two purchase choices.',
    'Прочитать русские слова и короткие фразы. / Read Russian words and short phrases.',
    'Нарисовать полезную вещь и подписать её. / Draw a useful thing and label it.'
  ],
  vocabulary: [
    { term: 'деньги / money', definition: 'то, что помогает людям обмениваться и выбирать. / what helps people exchange and choose.' },
    { term: 'цена / price', definition: 'сколько нимов нужно отдать за вещь. / how many nims you give for a thing.' },
    { term: 'выбор / choice', definition: 'когда можно взять одно, но не всё сразу. / when you can take one thing, not everything.' },
    { term: 'остаток / remainder', definition: 'сколько нимов остаётся после покупки. / how many nims remain after a purchase.' },
    { term: 'польза / usefulness', definition: 'чем вещь помогает человеку. / how the thing helps a person.' },
    { term: 'нимы / nims', definition: 'призовые токены курса Nimny Lab. / prize tokens of the Nimny Lab course.' }
  ],
  onlineTasks: [
    {
      id: 'reading-words',
      type: 'text',
      title: 'Слова дня / Words of the Day',
      prompt: 'Прочитай: деньги, цена, выбор, остаток, польза, нимы.',
      ruPrimaryText: 'Прочитай по-русски: деньги, цена, выбор, остаток, польза, нимы.',
      enHelpText: 'Read in Russian: money, price, choice, remainder, usefulness, nims.',
      hint: 'Сначала прочитай сама. Если трудно — попроси родителя помочь по слогам. / Try reading first; ask a parent for help with syllables if it is hard.',
      answer: 'Попытка чтения всех русских слов. / Attempt to read all the Russian words.'
    },
    {
      id: 'reading-sentences',
      type: 'text',
      title: 'Маленькое чтение / Short Reading',
      prompt:
        'Прочитай по-русски:\n1. Деньги помогают выбирать.\n2. У каждой вещи есть цена.\n3. Польза важнее красивой упаковки.\n4. Я считаю стоимость и остаток.',
      ruPrimaryText:
        'Прочитай по-русски 4 короткие фразы про деньги, цену, пользу и остаток.',
      enHelpText:
        '1. Money helps me choose.\n2. Every thing has a price.\n3. Usefulness is more important than pretty packaging.\n4. I add up the cost and find what remains.',
      hint: 'Русский — первый. Английский — только для смысла. / Russian first. English is only for meaning.',
      answer: 'Попытка чтения 4 русских фраз. / Attempted Russian reading.'
    },
    {
      id: 'math-1-purchase-remainder',
      type: 'math',
      title: 'Math 1 — Покупка и остаток / Purchase and Remainder',
      prompt:
        'У Аниты есть 100 нимов.\nОна выбрала книгу за 28 нимов, карандаш за 7 нимов и 3 наклейки по 6 нимов.\nСколько нимов останется?',
      ruPrimaryText:
        'У Аниты 100 нимов. Книга 28, карандаш 7, 3 наклейки по 6. Сколько останется?',
      enHelpText:
        'Anita has 100 nims. A book is 28, a pencil is 7, and 3 stickers are 6 nims each. How many nims remain? Find the total cost first, then subtract from 100.',
      helper: 'Сначала найди общую стоимость, потом вычти из 100. / Find the total cost first, then subtract from 100.',
      answer: '47',
      unit: 'нимов / nims'
    },
    {
      id: 'math-2-card-sets-fraction',
      type: 'math',
      title: 'Math 2 — Наборы и треть / Sets and One Third',
      prompt:
        'Анита сделала 4 набора карточек.\nВ каждом наборе 6 карточек.\nПотом она подарила 1/3 всех карточек Нимни.\nСколько карточек осталось?',
      ruPrimaryText:
        '4 набора × 6 карточек. Подарила 1/3. Сколько осталось?',
      enHelpText:
        '4 sets of 6 cards each. She gave 1/3 of all cards to Nimny. How many cards remain? Find all cards first, then find one third.',
      helper: 'Сначала всего карточек: 4 × 6. Потом 1/3 от этого числа. / Total first, then one third.',
      answer: '16',
      unit: 'карточек / cards'
    },
    {
      id: 'math-3-compare-choices',
      type: 'math',
      title: 'Math 3 — Сравни два выбора / Compare Two Choices',
      prompt:
        'У Аниты 80 нимов.\nВариант A: блокнот 35 нимов и ручка 18 нимов.\nВариант B: книга 42 нима и наклейки 15 нимов.\nВ каком варианте останется больше нимов и на сколько?',
      ruPrimaryText:
        '80 нимов. A: 35 + 18. B: 42 + 15. Где остаток больше и на сколько?',
      enHelpText:
        '80 nims. Option A: 35 + 18. Option B: 42 + 15. Which option leaves more nims, and by how many?',
      helper: 'Найди остаток для A и для B, потом сравни. / Find each remainder, then compare.',
      answer: 'Вариант A на 4 нима больше / Option A by 4 nims',
      unit: ''
    },
    {
      id: 'math-4-price-table',
      type: 'math',
      title: 'Math 4 — Таблица цен / Price Table',
      prompt:
        'Таблица цен:\nяблоко — 4 нима\nсок — 9 нимов\nтетрадь — 16 нимов\nкнига — 32 нима\n\nАнита купила 2 яблока, 1 сок и 1 тетрадь.\nСколько она потратила?\nЕсли у неё было 50 нимов, сколько осталось?\n\nЗапиши ответ через /: потратила / осталось. Например: 33 / 17.',
      ruPrimaryText:
        'Из таблицы: 2 яблока + 1 сок + 1 тетрадь. Стоимость? Остаток от 50? Запиши ответ через /: например 33 / 17.',
      enHelpText:
        'Use the price table: apple 4, juice 9, notebook 16, book 32. Anita bought 2 apples, 1 juice and 1 notebook. How much did she spend? If she had 50 nims, how many remain? Write the answer with a slash: spent / remain. Example: 33 / 17.',
      helper: 'Сначала 2 × 4 для яблок, потом сложи всё. Потом 50 минус стоимость. Ответ через /: потратила / осталось. / Multiply for apples, sum, then subtract. Format as spent / remain.',
      answer: '33 / 17',
      unit: 'нимов / nims'
    },
    {
      id: 'logic-need-vs-want',
      type: 'text',
      title: 'Логика — нужно или хочу? / Logic — Need or Want?',
      prompt:
        'Что лучше купить сначала: «нужное» или «то, что я просто хочу»?\nОтветь 1–2 предложениями и объясни почему.',
      ruPrimaryText:
        'Что лучше купить сначала: «нужное» или «то, что я просто хочу»? Объясни.',
      enHelpText:
        'What should you buy first: a "need" or a "want"? Answer in 1–2 sentences and explain why.',
      placeholder: 'Сначала нужно купить ..., потому что ...',
      answer:
        'Принимается ответ, где «нужное» идёт первым и есть короткое объяснение. Пример: «Сначала нужное, потому что без него нельзя обойтись.»'
    },
    {
      id: 'spelling-money',
      type: 'text',
      title: 'Правописание / Spelling',
      prompt:
        'Напиши или обведи:\nденьги\nцена\nвыбор\nостаток\n\nВставь буквы:\nде_ьги\nце_а\nвы_ор\nост_ток',
      ruPrimaryText: 'Напиши слова и вставь пропущенные буквы.',
      enHelpText: 'Write the words and fill in the missing letters: деньги, цена, выбор, остаток.',
      answer: 'деньги, цена, выбор, остаток.'
    },
    {
      id: 'creative-useful-thing',
      type: 'text',
      title: 'Моя полезная вещь / My Useful Thing',
      prompt:
        'Нарисуй одну полезную вещь. Подпиши:\nЭто ______.\nОна (он) помогает ______.',
      ruPrimaryText: 'Нарисуй полезную вещь и подпиши.',
      enHelpText: 'Draw one useful thing and label it: "This is ___. It helps ___."',
      answer:
        'Open-ended. Принимается, если есть рисунок и подпись «Это ... Она (он) помогает ...».'
    },
    {
      id: 'speech-explain-choice',
      type: 'text',
      title: 'Объясни родителю / Explain to Parent',
      prompt: 'Скажи по-русски: почему деньги помогают выбирать?',
      ruPrimaryText: 'Скажи по-русски одну фразу: почему деньги помогают выбирать?',
      enHelpText: 'Say in Russian one sentence about why money helps people choose.',
      answer:
        'Принимаются ответы вроде «Деньги помогают сравнивать цену и пользу.» «Деньги помогают купить нужное.» Лучший: «Деньги помогают выбирать, но главное — польза.»'
    }
  ],
  notebookInstructions: [
    'Если нет принтера:',
    '1. Напиши слова дня.',
    '2. Реши 4 math задачи с шагами в тетради.',
    '3. Ответь на вопрос «нужно или хочу?» 1–2 предложениями.',
    '4. Напиши 4 слова на правописание.',
    '5. Нарисуй полезную вещь и подпиши.',
    '6. Скажи родителю по-русски, почему деньги помогают выбирать.'
  ],
  printWorksheet: {
    title: 'Деньги, выбор и ценность / Money, Choice and Value',
    subtitle: 'Неделя 1 · День 1 / Week 1 · Day 1',
    pages: [
      {
        title: 'Маршрут дня · Слова · Чтение / Route, Words and Reading',
        blocks: [
          { type: 'text', text: 'Миссия: Анита учится считать стоимость, остаток и сравнивать два варианта. / Mission: Anita learns to add up cost, find a remainder, and compare two choices.' },
          { type: 'list', items: [
            'Слова дня: деньги, цена, выбор, остаток, польза, нимы. / Words of the day: money, price, choice, remainder, usefulness, nims.',
            'Маленькое чтение — 4 короткие фразы по-русски (см. экран). / Short Russian reading — 4 sentences (see screen).',
            'English help мелким шрифтом — только если трудно. / English help in small print — only if it is hard.'
          ] }
        ]
      },
      {
        title: 'Математика и логика / Math and Logic',
        blocks: [
          { type: 'text', text: 'Показывай шаги задачи. Покажи общую стоимость и остаток. / Show your work. Show total cost and remainder.' },
          { type: 'list', items: [
            'Math 1. 100 нимов. Книга 28 + карандаш 7 + 3 × 6 наклеек. Сколько останется? Шаги: 3 × 6 = ___; 28 + 7 + ___ = ___; 100 − ___ = ___. / How many nims remain?',
            'Math 2. 4 набора × 6 карточек. Подарила 1/3. Сколько осталось? Шаги: 4 × 6 = ___; 1/3 от ___ = ___; ___ − ___ = ___. / How many cards remain?',
            'Math 3. 80 нимов. A: 35 + 18 = ___, остаток ___. B: 42 + 15 = ___, остаток ___. Где остаток больше и на сколько? / Compare the two remainders.',
            'Math 4. Таблица цен: яблоко 4, сок 9, тетрадь 16, книга 32. Купила 2 яблока, 1 сок, 1 тетрадь. Стоимость ___, остаток от 50 ___. / Use the price table.',
            'Логика: «нужное» или «хочу» — что купить первым и почему? Ответ 1–2 предложения. / Logic: need vs want — answer in 1–2 sentences.'
          ] }
        ]
      },
      {
        title: 'Правописание, рисунок и parent check / Spelling, Drawing and Parent Check',
        blocks: [
          { type: 'list', items: [
            'Правописание: напиши деньги, цена, выбор, остаток. Вставь буквы: де_ьги, це_а, вы_ор, ост_ток. / Spelling.',
            'Нарисуй полезную вещь и подпиши: «Это ___. Она (он) помогает ___.» / Draw a useful thing and label it.',
            'Скажи родителю по-русски: «Деньги помогают ...» / Say in Russian to a parent: "Money helps ..."',
            'Parent Check: считать стоимость, остаток, сравнение, объяснение выбора. / Parent Check.',
            'Notebook Mode fallback — если нет принтера, всё то же в тетради. / Notebook Mode fallback — without a printer, do everything in the notebook.'
          ] }
        ]
      }
    ]
  },
  parentGuide: {
    goal:
      'Анита понимает, что деньги — это инструмент выбора, и умеет считать общую стоимость, остаток и сравнивать варианты.',
    mathGoal:
      'U.S. Grade 3: двухшаговые задачи (стоимость → остаток), умножение и деление как шаги внутри задачи, сравнение двух остатков, простая дробь 1/3, работа с таблицей цен.',
    russianGoal:
      'Прочитать слова: деньги, цена, выбор, остаток, польза, нимы.',
    readingWritingGoal:
      'Прочитать 4 короткие фразы по-русски, написать 4 слова, подписать рисунок.',
    practicalGoal:
      'Сравнить два варианта покупки и объяснить выбор «нужное / хочу».',
    whatThisLessonTeaches:
      'Анита учится считать стоимость в несколько шагов, сравнивать два варианта по остатку и понимать, что выбор зависит не только от цены, но и от пользы.',
    keyMessage:
      'Сначала польза, потом цена. Умный покупатель считает стоимость, остаток и думает, что важнее — «нужное» или «хочу».',
    howToExplain:
      'Возьмите 100 бумажных нимов и реальные предметы (книга, карандаш, наклейка). Вместе посчитайте стоимость и сколько останется. Потом сравните два набора покупок.',
    exampleDialogue:
      'Parent: Если ты купишь книгу за 28 и карандаш за 7, сколько потратишь?\nChild: 35.\nParent: А если ещё 3 наклейки по 6?\nChild: 3 × 6 = 18. Всего 53.\nParent: Сколько останется от 100?\nChild: 47.',
    readingSupport:
      'Сначала Анита читает русские слова сама. Если трудно — родитель помогает по слогам. Английский помогает только понять смысл.',
    russianSpeechGoal:
      'Анита говорит по-русски: «Деньги помогают выбирать, но главное — польза.»',
    mathAnswerKey:
      'Math 1: 3 × 6 = 18; 28 + 7 + 18 = 53; 100 − 53 = 47 нимов.\nMath 2: 4 × 6 = 24; 1/3 от 24 = 8; 24 − 8 = 16 карточек.\nMath 3: A = 80 − (35 + 18) = 27; B = 80 − (42 + 15) = 23; A больше на 4.\nMath 4: 2 × 4 = 8; 8 + 9 + 16 = 33; 50 − 33 = 17 нимов.',
    spellingAnswerKey: 'деньги\nцена\nвыбор\nостаток',
    answerKey: [
      { taskId: 'reading-words', answer: 'Answer: попытка чтения всех русских слов.\nSolution: Анита читает по очереди; родитель подсказывает только по слогам.\nParent note: Не торопить; засчитывать честную попытку.' },
      { taskId: 'reading-sentences', answer: 'Answer: попытка чтения 4 фраз по-русски.\nSolution: Каждая фраза — отдельно. Английский — после русской попытки.\nParent note: Главное — понимание смысла, не идеальная фонетика.' },
      { taskId: 'math-1-purchase-remainder', answer: 'Answer: 47 нимов.\nSolution: 3 × 6 = 18; 28 + 7 + 18 = 53; 100 − 53 = 47.\nParent note: Это двухшаговая задача: сначала общая стоимость, потом остаток.' },
      { taskId: 'math-2-card-sets-fraction', answer: 'Answer: 16 карточек.\nSolution: 4 × 6 = 24; 1/3 от 24 = 8; 24 − 8 = 16.\nParent note: Проверить, понимает ли ребёнок, что 1/3 — это одна из трёх равных частей.' },
      { taskId: 'math-3-compare-choices', answer: 'Answer: Вариант A оставляет больше нимов на 4.\nSolution: A: 35 + 18 = 53, 80 − 53 = 27. B: 42 + 15 = 57, 80 − 57 = 23. 27 − 23 = 4.\nParent note: Это сравнение двух вариантов, не просто счёт.' },
      { taskId: 'math-4-price-table', answer: 'Answer: потратила 33 нима, осталось 17.\nSolution: 2 × 4 = 8; 8 + 9 + 16 = 33; 50 − 33 = 17.\nParent note: Ребёнок должен читать таблицу и делать два действия.' },
      { taskId: 'logic-need-vs-want', answer: 'Answer: сначала «нужное», потом «хочу».\nSolution: Принимается любое объяснение, что «нужное» (еда, тетрадь для школы) важнее, а «хочу» можно подождать.\nParent note: Поощряйте рассуждение, а не точную формулу.' },
      { taskId: 'spelling-money', answer: 'Answer: деньги, цена, выбор, остаток.\nSolution: Вставленные буквы: н, н, б, а.\nParent note: Лёгкие ошибки разрешены; главное — узнаваемые слова.' },
      { taskId: 'creative-useful-thing', answer: 'Answer: рисунок + подпись «Это … Она (он) помогает …».\nSolution: Open-ended.\nParent note: Принимаются любые полезные вещи: книга, рюкзак, бутылка воды.' },
      { taskId: 'speech-explain-choice', answer: 'Answer: одна осмысленная русская фраза про выбор.\nSolution: «Деньги помогают сравнить цену и пользу.»\nParent note: Не требовать идеального произношения.' }
    ],
    check: [
      'Анита прочитала или попыталась прочитать русские слова и фразы.',
      'Анита решила 4 math задачи с показом шагов.',
      'Анита объяснила «нужное / хочу».',
      'Анита написала 4 слова и подписала рисунок.',
      'Анита сказала одну фразу по-русски о деньгах.'
    ],
    spellingCheck:
      'Проверить буквы в словах деньги, цена, выбор, остаток.',
    readingCheck:
      'Не требовать идеального чтения. Засчитывается честная попытка чтения по-русски.',
    practicalCheck:
      'Анита может объяснить, почему «нужное» обычно идёт первым.',
    drawingCriteria:
      'Рисунок понятен. Есть подпись «Это …» и «помогает …». Ошибки в письме допустимы, если смысл понятен.',
    commonMistakes: [
      '«Деньги — это главное.» Правильно: «Главное — польза.»',
      '«Хочу — это плохо.» Правильно: «Хочу не плохо, но сначала нужное.»',
      'Считать только первый шаг и забыть про остаток. Правильно: дойти до второго шага.'
    ],
    discussionPrompts: [
      'Что важнее: красивая упаковка или польза?',
      'Что ты выбрала бы из таблицы цен, если бы было 50 нимов?',
      'Почему иногда лучше не брать всё сразу?'
    ],
    ifEasy:
      'У Аниты 120 нимов. Книга 28, карандаш 7, 4 наклейки по 6 и сок 9. Сколько останется? (Ответ: 120 − (28 + 7 + 24 + 9) = 52.)',
    ifDifficult:
      'Оставьте только Math 1 и Math 4 и считайте вместе по шагам.',
    rewardGuidance:
      'Базовая награда — 3 нима. Бонус за самостоятельное русское чтение или за правильное двухшаговое решение.',
    parentOnlyNotes:
      'Не говорить, что нимы равны реальным деньгам. Не использовать слова «оплата», «зарплата», «заработала».',
    tokenReward:
      'Нимы — это призовые токены курса, а не оплата.'
  },
  reward: {
    unit: 'nim',
    unitName: 'ним',
    base: 3,
    max: 5,
    label: 'Награда за миссию',
    display: '3 нима',
    bonuses: [
      { id: 'two-step-math', label: 'Бонус за самостоятельное двухшаговое решение', amount: 1 },
      { id: 'good-russian-reading-attempt', label: 'Бонус за хорошую попытку чтения по-русски', amount: 1 }
    ]
  },
  completionRequirements: {
    requiredTaskIds: [
      'reading-words',
      'reading-sentences',
      'math-1-purchase-remainder',
      'math-2-card-sets-fraction',
      'math-3-compare-choices',
      'math-4-price-table',
      'logic-need-vs-want',
      'spelling-money',
      'creative-useful-thing',
      'speech-explain-choice'
    ],
    allowNotebookCompletion: true
  },
  nextLessonId: 'anita-week-1-day-2'
};

export default day1;
